// src/components/SearchBar.jsx
import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, FileText } from 'lucide-react';
import AuthContext from '../contexts/AuthContext';

/**
 * Search bar component for searching across the platform including document content
 */
const SearchBar = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // Focus on input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!query.trim()) return;
    
    // Build search URL with query params
    const searchParams = new URLSearchParams();
    searchParams.append('q', query.trim());
    searchParams.append('type', searchType);
    
    if (user?.school?.id || user?.schoolId) {
      searchParams.append('schoolId', user.school?.id || user.schoolId);
    }
    
    // Navigate to search results page
    navigate(`/search?${searchParams.toString()}`);
    
    // Close search bar
    if (onClose) onClose();
  };
  
  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="relative flex items-center">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search across titles, descriptions, and document content..."
          className="pl-10 pr-16 py-2 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 dark:text-gray-300 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="all">All</option>
            <option value="courses">Courses</option>
            <option value="professors">Professors</option>
            <option value="materials">Materials</option>
          </select>
          
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="p-1 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-full"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>
      
      <div className="mt-2 flex justify-between">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default SearchBar;